import React, { useState } from "react";
import Modal from "../Common/Modal";
import CheckBox from "../Buttons/CheckBox";
import MtiButton from "../Buttons/MtiButton";
import "./EulaModal.css"

type Props = {
  show: boolean;
  onClickOk: () => void;
};

export default function EulaModal({ show, onClickOk }: Props) {
  const [agree, setAgree] = useState(false);

  return (
    <Modal hideOnBackdropClick={false} show={show} className="eula-modal">
      <div className="eula-modal-header">Terms and Service</div>
      <Modal.Body className="eula-modal-body">
        <div className="eula-content">
          <p className="eula-title">
            FORMULATRIX SOFTWARE LICENSE AND MAINTENANCE AGREEMENT
          </p>
          <p>
            This Software License and Maintenance Agreement (the
            &ldquo;Agreement&rdquo;) is between{" "}
            <strong>Formulatrix, Inc.</strong>, with its principal place of
            business at 10 Deangelo Drive, Bedford, MA, 01730, and the customer
            who purchased the software (the &ldquo;Customer&rdquo;).
          </p>
          <p className="eula-heading-1">
            <strong>TERMS AND CONDITIONS</strong>
          </p>
          <p>
            Formulatrix and Customer hereby agree that the following terms and
            conditions will apply to each Software license granted under this
            Agreement. Software licenses or Software Maintenance Services
            (&ldquo;SMS&rdquo;) may be ordered by Customer or by any Subsidiary,
            by signing a Formulatrix Quotation or amendment to this Agreement
            that references this Agreement and identifies a particular Site (as
            defined below). Upon acceptance by Formulatrix, such Quotation or
            amendment shall be deemed subject to and part of this Agreement (an
            &ldquo;Accepted Order&rdquo;) and, in the case of a Subsidiary,
            shall constitute such Subsidiary&rsquo;s agreement to be bound by
            the terms of this Agreement. Customer further agrees that any
            software used or made available to its Subsidiaries shall be subject
            to the terms of this Agreement.{" "}
          </p>
          <p className="eula-heading-1">
            <strong>1.0 DEFINITIONS</strong>
          </p>
          <p>
            <strong>1.1</strong> <strong>&ldquo;Computer&rdquo;</strong> shall
            mean a computer owned by Customer. For the avoidance of doubt, this
            does include laptops owned by Customer and used by a Customer
            employee off-site, but does not include employee&rsquo;s home
            computer.
          </p>
          <p>
            <strong>1.2</strong> <strong>&ldquo;Database&rdquo;</strong> shall
            mean the repository that Software uses as its primary information
            store.
          </p>
          <p>
            <strong>1.3</strong>{" "}
            <strong>&ldquo;Integrated Instrument&rdquo;</strong> shall mean a
            physical device, other than a computer (e.g. imager or liquid
            handler), which produces information for, or consumes information
            from Software.
          </p>
          <p>
            <strong>1.4</strong> <strong>&ldquo;Site&rdquo;</strong>
            <strong> </strong>means a single geographic facility of Customer to
            be specified in the Accepted Order or subsequently agreed by mutual
            consent in writing. Licenses granted hereunder shall not be in force
            unless and until a single Site has been identified in writing.
            Customer may change the Site in connection with relocation of its
            Integrated Instruments, by providing written notice to Formulatrix.
            In the case that only an Instrument License is obtained, no Site
            need be identified in the Accepted Order.
          </p>
          <p>
            <strong>1.5</strong> <strong>&ldquo;Site Database&rdquo;</strong>{" "}
            means the Database that serves one or more Integrated Instruments,
            all of which must be located at the single Site.{" "}
          </p>
          <p>
            <strong>1.6 License Types:</strong>
          </p>
          <p style={{ paddingLeft: "36pt" }}>
            <strong>1.6.1</strong> <strong>&ldquo;Seat License&rdquo;</strong>{" "}
            shall mean the right to install Software on a single Computer, to be
            used by a User physically present at the Computer, to access the
            single Site Database. For purposes of clarification, a Seat License
            may be for a Computer physically located away from the Site, but may
            only be used to access the single Site Database.
          </p>
          <p style={{ paddingLeft: "36pt" }}>
            <strong>1.6.2</strong>{" "}
            <strong>&ldquo;Concurrent License&rdquo;</strong> shall mean the
            right to{" "}
            <em>
              <u>install</u>
            </em>{" "}
            Software on an unlimited number of Computers, to be used by User(s)
            physically present at the Computers, provided that (a) the Software
            may only be used to access the single Site Database, and (b) the
            Software may be{" "}
            <em>
              <u>run</u>
            </em>{" "}
            on only one Computer at a time for each Concurrent License granted.
          </p>
          <p style={{ paddingLeft: "36pt" }}>
            <strong>1.6.3</strong> <strong>&ldquo;Site License&rdquo;</strong>{" "}
            shall mean the right to install Software on an unlimited number of
            Computers, to be used by unlimited User(s) at the same time, but
            only to access the single Site Database.
          </p>
          <p style={{ paddingLeft: "36pt" }}>
            <strong>1.6.4</strong>{" "}
            <strong>&ldquo;Web Server License&rdquo;</strong> shall mean the
            right to install Software on a single Computer to be used by
            unlimited User(s) via HTTP, to access the single Site Database.
          </p>
          <p style={{ paddingLeft: "36pt" }}>
            <strong>1.6.5</strong>{" "}
            <strong>&ldquo;Instrument License&rdquo;</strong> shall mean the
            right to use Software intended to control an instrument with one
            such instrument. If the Accepted Order does not specify a license
            type from the types listed above in Sections 1.6.1 through 1.6.4,
            then the license type is deemed an Instrument License.
          </p>
          <p>
            <strong>1.7</strong> <strong>&ldquo;Software&rdquo;</strong> shall
            mean the object code version of Formulatrix&rsquo; proprietary
            computer program(s) identified in the Quotation and including any
            accompanying Documentation and subsequent Releases delivered to
            Customer.{" "}
          </p>
          <p>
            <strong>1.8</strong> <strong>&ldquo;Subsidiary&rdquo;</strong> shall
            mean a corporation or other entity in which the Customer owns more
            than a 50% interest as of the date of this Agreement.
          </p>
          <p>
            <strong>1.9</strong> <strong>&ldquo;Quotation&rdquo;</strong>
            <strong> </strong>shall mean Formulatrix&rsquo; standard Quotation
            for quoting prices of Software licenses and SMS.
          </p>
          <p>
            <strong>1.10</strong> <strong>&ldquo;Users&rdquo;</strong> shall
            mean employees and agents of Customer who are authorized by Customer
            to use the Software in accordance with this Agreement.
          </p>
          <p className="eula-heading-1">
            <strong>2.0 SOFTWARE LICENSE GRANT</strong>
          </p>
          <p>
            AGREEMENT NUMBER _________________________
            <br />
            <strong>2.1 </strong>
            <strong>
              <u>General</u>
            </strong>{" "}
            Formulatrix grants and Customer accepts a perpetual, fully paid-up,
            nonexclusive, nontransferable license to use the Software specified
            in the Accepted Order for the license type designated in the
            Accepted Order solely for the Customer&rsquo;s own internal business
            operations. Further, for any Seat License, Concurrent License, Site
            License or Web Server License, the license is expressly limited to
            use solely with the Site Database of the Site specified in the
            Accepted Order. Customer shall not (i) use the Software for time
            sharing, rental, or service bureau use, (ii) modify, enhance or
            create derivative works of the Software, or (iii) decompile or
            disassemble the Software, or otherwise attempt to access the source
            code of the Software.{" "}
          </p>
          <p>
            <strong>2.2 </strong>
            <strong>
              <u>Copies</u>
            </strong>{" "}
            The Customer may make one archival or back-up copy of the Software,
            provided that Formulatrix and Formulatrix&rsquo; vendors&rsquo;
            copyright and proprietary notices on the Software are included on
            such copy.
          </p>
          <p>
            <strong>2.3 </strong>
            <strong>
              <u>Proper Use of Software</u>
            </strong>{" "}
            The Customer acknowledges that the continued integrity of the
            Software and Formulatrix&rsquo; performance of its obligations
            described in this Agreement are dependent upon the proper use and
            maintenance of the Software by Customer. Proper use and maintenance
            means that Customer will (i) use the Software in accordance with
            this Agreement and the documentation supplied by Formulatrix and
            (ii) follow Formulatrix&rsquo; instructions for installing new
            releases and for correcting and circumventing software defects.{" "}
          </p>
          <p>
            <strong>2.4 </strong>
            <strong>
              <u>Ownership</u>
            </strong>{" "}
            Formulatrix and its suppliers reserve all right, title and interest
            in and to the Software (including all intellectual property rights
            embodied therein) and Customer&rsquo;s rights in the Software are
            limited to those expressly granted in this Agreement.{" "}
          </p>
          <p>
            <strong>2.5 </strong>
            <strong>
              <u>Proprietary Information</u>
            </strong>{" "}
            Formulatrix and Customer may receive technical, financial, trade
            secret, confidential or proprietary information of the other party
            and its licensors (&ldquo;Proprietary Information&rdquo;) in
            connection with this Agreement. The receiving party shall (i)
            protect such Proprietary Information to the same degree it protects
            its own Proprietary Information, but with no less than a reasonable
            degree of care, (ii) maintain and adopt reasonable measures to
            ensure that its employees and agents comply with the terms of this
            Agreement, (iii) use such Proprietary Material only for the purposes
            set forth in this Agreement, and (iv) limit use of and access to the
            Proprietary Material to its employees and agents that need to use
            the Proprietary Material on behalf of the receiving party. The
            receiving party shall not disclose any Proprietary Information to
            any third party without the prior written consent of the other
            party. The following shall not be deemed to be Proprietary
            Information: information that is generally available in the public
            domain, that is already possessed by the receiving party (provided
            that such prior possession can be demonstrated from written records
            predating the date of the disclosure), or that is received from a
            third party without violating any duty owed by such third party to
            the disclosing party. Notwithstanding anything to the contrary,
            Formulatrix&rsquo; Proprietary Information includes without
            limitation the Software and any materials delivered to Customer with
            the Software.
          </p>
          <p>
            <strong>2.6 </strong>
            <strong>
              <u>Delivery</u>
            </strong>{" "}
            Within 2 months of execution of this agreement Formulatrix shall
            deliver to Customer executable copies of the licensed products.
            Customer must approve the Licensed products within fifteen (15)
            business days of receipt thereof.
          </p>
          <p className="eula-heading-1">
            <strong>3.0 TERMINATION OF LICENSES</strong>
          </p>
          <p>
            <strong>3.1 </strong>Formulatrix may terminate any license and/or
            SMS in the event that Customer materially breaches this Agreement.
            In such event, Formulatrix shall give Customer written notice
            specifying the nature of the breach, and, if Customer fails to cure
            the breach within fifteen (15) days of receipt of such notice,
            Formulatrix may thereafter terminate the license(s) and/or SMS
            effective immediately by giving notice. Notwithstanding any other
            provision hereof, Formulatrix shall have the right to terminate any
            license and/or SMS effective immediately upon written notice to
            Customer in the event of any breach by Customer of any of the
            provisions of Section 2. Termination of any license shall not (i)
            relieve Customer&rsquo;s obligation to pay all license, SMS and
            other fees due under this Agreement, or (ii) entitle Customer to a
            refund of any license, SMS, or other fees paid prior to such
            termination.<u> </u>{" "}
          </p>
          <p className="eula-heading-1">
            <strong>4.0</strong>{" "}
            <strong>SOFTWARE MAINTENANCE SERVICE (SMS)</strong>
          </p>
          <p>
            <strong>4.1 </strong>
            <strong>
              <u>General</u>
            </strong>{" "}
            For the Initial Maintenance Term and thereafter provided that the
            Customer has elected SMS and has made timely payment of the SMS
            fee(s), Formulatrix will provide the SMS specified in this Section 4
            for the applicable Software.{" "}
          </p>
          <p>
            <strong>4.2 </strong>
            <strong>
              <u>Initial Maintenance Term</u>
            </strong>{" "}
            The Initial Maintenance Term shall commence on the delivery of the
            Software and remain in effect for one (1) year. Thereafter the
            Maintenance Term shall renew automatically for one (1) year periods
            for the length of the Software license term as described in the
            Accepted Order, unless terminated by Customer as of the annual
            anniversary date of the Maintenance Term (&ldquo;Renewal
            Date&rdquo;) by providing Formulatrix not less than thirty (30) days
            written notice prior to the Renewal Date. Formulatrix shall provide
            Customer with written notice of the fee for the subsequent
            Maintenance Term not less than sixty (60) days prior to the Renewal
            Date. Formulatrix may increase its annual SMS fee for subsequent
            Maintenance Terms, but the amount of each such increase shall not
            exceed ten percent (10%) of the standard fee for the immediately
            preceding Maintenance Term. Failure to make timely payment of any
            SMS fee shall not constitute a waiver by Formulatrix of such fee,
            the obligation to make such payment, or a valid termination of SMS.
            Formulatrix reserves the right to refuse to provide SMS while any
            accrued SMS fees remain unpaid. No refunds of maintenance fees will
            be provided for termination of services during the annual term.
            Pursuant to the terms of this agreement, as long as the Maintenance
            term remains in effect, Formulatrix shall provide to Customer all
            enhancements to the licensed products at the same time that
            Formulatrix makes such enhancements available to other customers
          </p>
          <p>
            <strong>4.3 </strong>
            <strong>
              <u>Non-Continuous Coverage</u>
            </strong>{" "}
            In the event Customer elects not to maintain continuous SMS
            services, Formulatrix may, in its discretion, refuse to provide any
            SMS to Customer until payment for the period of discontinuity is
            made current.
          </p>
          <p>
            <strong>4.4 </strong>
            <strong>
              <u>Definitions</u>
            </strong>{" "}
            For the purposes of this Section 4, the following definitions shall
            apply: &ldquo;Defect&rdquo; means a material error in program logic
            attributable to Formulatrix which prevents the performance of a
            principal computing function as set forth in Formulatrix&rsquo;
            Documentation for the Software. &ldquo;Major Release&rdquo; means a
            new version of the Software with new features and/or significant
            enhancements to existing modules. &ldquo;Point Release&rdquo; means
            a version of the Software occurring between Major Releases, and may
            contain Defect corrections as well as new features and enhancements.
            &ldquo;Release&rdquo; may refer to a Major or Point Release.
            &ldquo;Documentation&rdquo; means the user instructions made
            available with the Software to Customer by Formulatrix.{" "}
          </p>
          <p>
            <strong>4.5 </strong>
            <strong>
              <u>Services</u>
            </strong>{" "}
            (a) Formulatrix will remedy Defects by using reasonable efforts to
            (i) incorporate a permanent Defect correction in the next Release of
            the Software or (ii) provide a fix, patch or workaround procedure.
            Customers will be notified of the availability of new Releases. All
            Releases shall be deemed to be Software hereunder. (b) Formulatrix
            will support only the current Release of the Software and all
            Releases since and including the Major Release preceding the current
            Major Release. Customer must maintain all associated computer
            hardware and operating system software at the latest Release level
            deemed necessary by Formulatrix. Consultative Support Services from
            Formulatrix may be available to Customer under separate agreement.
            When Formulatrix determines that any requested assistance has
            exceeded a reasonable level, Formulatrix may supply a quotation for
            additional Consultative Support Service. Custom development work,
            and maintenance thereof, is not covered by maintenance and shall be
            provided only as Consultative Support Services. Any customization
            performed by Formulatrix shall not be &ldquo;work for hire&rdquo;
            and all works of authorship and all developments made, conceived,
            created, discovered, invented or reduced to practice in the delivery
            of assistance by Formulatrix or the performance of Consultative
            Support Services, are and shall remain the sole and absolute
            property of Formulatrix. To the extent that any software is
            developed by Formulatrix in connection with delivery of assistance
            or the performance of Consultative Support Services, such software
            shall be deemed part of the &ldquo;Software for the purposes&rdquo;
            of Sections 2, 3, 6 and 7 hereof.{" "}
          </p>
          <p className="eula-heading-1">
            <strong>5.0 PAYMENT</strong>
          </p>
          <p>
            <strong>5.1 </strong>
            <strong>
              <u>Payment and Taxes</u>
            </strong>
            <strong> </strong>In exchange for the rights and services provided
            hereunder, Customer shall pay the license fees, the applicable SMS
            fees, and any other fees indicated on the Quotation. Payment must be
            made within thirty (30) days of date of invoice. Customer shall pay
            all excise, sales, use, customs, value-added and other taxes, fees,
            levies, withholdings, or duties (&ldquo;Taxes&rdquo;), which arise
            out of this Agreement, excluding taxes measured by
            Formulatrix&rsquo; net income. Payments to Formulatrix shall be made
            without deduction for Taxes or shall be grossed-up to provide
            Formulatrix the same amount after such Tax as it would have received
            without the imposition of such Tax, together with tax receipts or
            similar evidence of any Tax payment by Customer. Customer agrees
            that, notwithstanding any other provisions of this Agreement, the
            license fees specified hereunder have been fully earned by
            Formulatrix upon delivery of the Software, and such license fees are
            due and payable to Formulatrix (without any further performance on
            the part of Formulatrix) in accordance with the provisions of this
            Agreement. All such payments will be made without setoff,
            counterclaim, recourse or other defense.
          </p>
          <p>
            <strong>5.2 </strong>
            <strong>
              <u>Right to Assign</u>
            </strong>{" "}
            Customer agrees that Formulatrix may, without notice, assign its
            rights to receive any and all payments due hereunder to a third
            party financier (&ldquo;Assignee&rdquo;). Such assignment shall not
            affect or release Formulatrix from its obligations and liabilities
            hereunder to Customer, however Customer further agrees that, in the
            event of any such assignment, the Assignee shall not be chargeable
            with or assume any of the obligations or liabilities of Formulatrix.
            Customer shall, in accordance with the terms of this Agreement and
            on instruction from Formulatrix, pay Assignee all amounts due
            hereunder without deduction or offset.
          </p>
          <p>
            <strong>5.3 </strong>
            <strong>
              <u>Remedies</u>
            </strong>{" "}
            In the event Customer fails to make any payment to Formulatrix or
            its Assignee when due, which failure continues for fifteen (15)
            days; in addition to any other rights and remedies available to
            Formulatrix or Assignee, all amounts owed by Customer hereunder
            shall become immediately due and payable and, in addition,
            Formulatrix or its Assignee may recover reasonable attorney&rsquo;s
            fees and legal expenses in exercising any of its rights and remedies
            upon default. Formulatrix or such Assignee will notify Customer of
            any failure to receive a payment when due, but Customer&rsquo;s
            failure to receive notice will not excuse a default or limit the
            remedies available to Formulatrix or Assignee as described herein.{" "}
          </p>
          <p className="eula-heading-1">
            <strong>6.0 WARRANTY</strong>
          </p>
          <p>
            <strong>6.1 </strong> <u>Warranty Period</u> (a) Formulatrix
            warrants, for period of thirty (30) days from delivery
            (&ldquo;Warranty Period&rdquo;), that each item of Software shall be
            free from Defects. Customer&rsquo;s exclusive remedy and
            Formulatrix&rsquo; sole obligation shall be, at Formulatrix&rsquo;
            option in each case, to either (i) correct or circumvent any Defect
            reported to Formulatrix within the Warranty Period or (ii) refund an
            amount equal to the depreciated license fee paid by Customer for the
            copy of Software at issue during the then-current license term
            (calculated on a straight-line basis over the period of the Term,
            but not to exceed three years) and terminate licenses granted for
            such copy; provided, however, that: (A) Customer shall promptly
            notify Formulatrix of any errors discovered and shall furnish to
            Formulatrix adequate supporting documentation, details, and
            assistance to substantiate and to assist Formulatrix in the
            identification and detection of such error; and (B) the error can be
            reproduced on properly functioning equipment controlled by
            Formulatrix. (b) Formulatrix warrants that it shall make
            commercially reasonable efforts to provide SMS, but does not warrant
            that it can or will correct all Defects or solve all problems.
            Customer&rsquo;s exclusive remedy and Formulatrix&rsquo; sole
            obligation shall be, at its option in each case, to either (i)
            re-perform the services that do not conform to such warranty or (ii)
            refund the applicable fees paid for the services (c) Formulatrix
            shall have no responsibilities under this Section in the event that
            (i) the Software has not been used in accordance with Section 2,
            (ii) the Software been damaged or altered by accident, neglect,
            misuse or other abuse or (iii) the claimed defect has been caused,
            in whole or in part, by other than Formulatrix, or a third party
            application, or configurations provided by third parties.{" "}
          </p>
          <p>
            <strong>6.2 </strong>
            <strong>
              <u>EXCLUSION OF WARRANTIES</u>
            </strong>
            <strong> </strong>EXCEPT FOR THE WARRANTIES SET FORTH IN SECTION 6.1
            ABOVE, FORMULATRIX DISCLAIMS ALL WARRANTIES EXPRESS OR IMPLIED, ORAL
            OR WRITTEN, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF
            MERCHANTABILITY, NONINFRINGEMENT AND FITNESS FOR A PARTICULAR
            PURPOSE. FORMULATRIX&rsquo; EXPRESS WARRANTIES SHALL NOT BE ENLARGED
            OR DIMINISHED AND NO OBLIGATION OR LIABILITY SHALL ARISE OUT OF ANY
            TECHNICAL ADVICE OR SERVICE PROVIDED BY FORMULATRIX IN CONNECTION
            WITH THE SOFTWARE OR SMS.{" "}
          </p>
          <p className="eula-heading-1">
            <strong>7.0 DAMAGES, INDEMNITY, RIGHTS AND OBLIGATIONS</strong>
          </p>
          <p>
            <strong>7.1 </strong>
            <strong>
              <u>LIMITATION OF LIABILITY</u>
            </strong>{" "}
            IN NO EVENT SHALL FORMULATRIX BE LIABLE TO THE CUSTOMER OR ANY OTHER
            PERSON OR ENTITY FOR SPECIAL, INDIRECT, INCIDENTAL, PUNITIVE OR
            CONSEQUENTIAL DAMAGES, OR ANY DAMAGES ARISING FROM THE LOSS OF USE,
            DATA OR PROFITS. FORMULATRIX SHALL NOT BE LIABLE FOR DAMAGES ARISING
            OUT OF OR RELATING TO THIS AGREEMENT, WHETHER IN TORT, CONTRACT,
            NEGLIGENCE, OR OTHERWISE ARISING MORE THAN ONE (1) YEAR PRIOR TO
            INSTITUTION OF A LEGAL PROCEEDING THEREON. IN NO EVENT SHALL
            FORMULATRIX&rsquo; LIABILITY ARISING OUT OF OR RELATING TO A
            SOFTWARE LICENSE EXCEED THE AMOUNT OF LICENSE FEES PAID BY CUSTOMER
            UNDER SUCH LICENSE DURING THE TWELVE (12) MONTH PERIOD PRECEDING THE
            DATE OF THE EVENT GIVING RISE TO LIABILITY FOR ANY DAMAGES. IN NO
            EVENT SHALL FORMULATRIX&rsquo; LIABILITY ARISING OUT OF OR RELATING
            TO SMS FOR A SOFTWARE LICENSE OR ANY OTHER SERVICES PROVIDED
            HEREUNDER EXCEED THE AMOUNT OF FEES PAID BY CUSTOMER FOR SUCH SMS OR
            OTHER SERVICES DURING THE TWELVE (12) MONTH PERIOD PRECEDING THE
            DATE OF THE EVENT GIVING RISE TO LIABILITY FOR ANY DAMAGES.{" "}
          </p>
          <p>
            <strong>7.2 </strong>
            <strong>
              <u>INFRINGEMENT INDEMNIFICATION</u>
            </strong>{" "}
            Formulatrix shall defend or settle any third party claim, suit or
            proceeding brought against Customer that any Software infringes a
            United States patent, copyright or trademark, and shall indemnify
            Customer against damages and costs finally awarded against Customer
            which result from such a claim, provided that: (i) Customer notifies
            Formulatrix in writing within seven (7) calendar days of learning of
            such claim, suit or proceeding and (ii) Customer gives Formulatrix
            sole control, and full authority, information and assistance in the
            defense or settlement of such claim, suit or proceeding. If any
            Software in whole or in part becomes, or if in Formulatrix&rsquo;
            opinion is likely to become the subject of a claim of intellectual
            property infringement, Formulatrix shall have the right, at its
            option and expense, to: (i) replace the Software with a
            non-infringing, functionally-compatible product; (ii) modify the
            Software so that it becomes non-infringing, (iii) obtain a license
            for Customer to use the Software, or (iv) if Formulatrix reasonably
            determines that options (i), (ii), and (iii) are not commercially
            practicable, to terminate the license and refund an amount equal to
            the depreciated license fee paid by Customer for the Software during
            the then-current license term (calculated on a straight line basis
            over the period of the Term, but not to exceed three years).
            Formulatrix will have no liability for any costs incurred or
            settlement entered into without its prior written consent. The
            foregoing indemnity shall not apply to claims, suits or proceedings
            that arise from (i) the use of the Software in combination with
            non-Formulatrix products, (ii) any modification of the Software by
            other than Formulatrix, or (iii) use of a version of the Software
            older than the Major Release of the Software immediately preceding
            the current Major Release. This Section 7.2 states the exclusive
            liability and entire obligation of Formulatrix, and the exclusive
            remedy of Customer, with respect to any claim, suit or proceeding
            involving actual or alleged infringement of any intellectual
            property rights in connection with this Agreement.
          </p>
          <p>
            <strong>7.3 </strong>
            <strong>
              <u>RESTRICTED RIGHTS</u>
            </strong>{" "}
            If Customer is acquiring the Software under a United States
            government contract, Customer agrees that it will include all
            necessary and applicable restricted rights legends on the Software
            and the documentation to protect Formulatrix&rsquo; proprietary
            rights under the FAR or other similar regulations of other federal
            agencies. The Customer agrees to always include such legends
            whenever the Software is, or is deemed to be, a deliverable under
            that contract.
          </p>
          <p>
            <strong>7.4 </strong>
            <strong>
              <u>COMPLIANCE WITH LAWS</u>
            </strong>
            <strong> </strong>Customer shall comply with all applicable laws and
            regulations including, without limitation, the export control laws
            of the United States.{" "}
          </p>
          <p className="eula-heading-1">
            <strong>8.0 GENERAL</strong>
          </p>
          <p>
            <strong>8.1 </strong>
            <strong>
              <u>Force Majeure</u>
            </strong>{" "}
            Either party&rsquo;s failure to perform its obligations hereunder
            shall not be deemed a breach of this Agreement if such failure is
            due to fire, strike, war, civil unrest, terrorist action, government
            regulations, acts of Nature, or other causes beyond the reasonable
            control of the party claiming force majeure. This provision shall
            not apply to Customer&rsquo;s obligation to pay any sums due under
            this Agreement.
          </p>
          <p>
            <strong>8.2 </strong>
            <strong>
              <u>Transfers and Assignment</u>
            </strong>{" "}
            Customer shall not assign, sublicense or otherwise transfer Software
            or Proprietary Information or otherwise assign its rights or
            obligations under this Agreement without the prior written consent
            of Formulatrix. For purposes of this section, an assignment or
            transfer shall include any change in majority ownership or control
            of Customer, including merger or other transfer of voting
            securities.{" "}
          </p>
          <p>
            <strong>8.3 </strong>
            <strong>
              <u>Waiver</u>
            </strong>{" "}
            Failure or delay of either party to exercise any right or remedy
            hereunder shall not constitute a waiver of rights or remedies under
            this Agreement.{" "}
          </p>
          <p>
            <strong>8.4 </strong>
            <strong>
              <u>Governing Law</u>
            </strong>{" "}
            This Agreement shall be governed by and construed in accordance with
            the laws of the Commonwealth of Massachusetts, excluding its
            conflicts of laws rules and the United Nations Convention on
            Contracts for the International Sale of Goods, regardless of where
            any action may be brought. The Customer hereby agrees to submit to
            the non-exclusive jurisdiction of the state and federal courts of
            the Commonwealth of Massachusetts.
          </p>
          <p>
            <strong>8.5 </strong>
            <strong>
              <u>Entire Agreement</u>
            </strong>{" "}
            This Agreement and any Accepted Orders constitute the exclusive
            statement of the terms and conditions between the parties with
            respect to the matters set forth herein, and supersede all other
            agreements, negotiations, representations, and proposals, written
            and oral. This Agreement and any Accepted Order may only be amended
            in a writing signed by both parties. Any additional or conflicting
            provisions contained in Customer&rsquo;s purchase order shall not
            apply and are rejected by Formulatrix. In the event of a conflict
            between this Agreement and any Accepted Order, the order of
            precedence shall be: (a) any provisions in the Accepted Order
            inconsistent with this Agreement with a citation to the Section
            being modified and (b) this Agreement.
          </p>
          <p>
            <strong>8.6 </strong>
            <strong>
              <u>Severability</u>
            </strong>{" "}
            If any court of competent jurisdiction holds any provision of this
            Agreement unenforceable or inoperative, either in whole or in part,
            the remaining provisions shall be given full force and effect to the
            extent not inconsistent with the original terms of this Agreement.{" "}
          </p>
          <p>
            <strong>8.7 </strong>
            <strong>
              <u>Notices</u>
            </strong>{" "}
            Any notice given hereunder shall be sent in writing to the other
            party&rsquo;s business address set forth above or to such other
            party and address as such party shall most recently have designated
            in writing. Notices directed to Formulatrix shall be sent
            &ldquo;Attention: Chief Financial Officer&rdquo;.
          </p>
          <p>
            <strong>8.8 </strong>
            <strong>
              <u>Publicity</u>
            </strong>{" "}
            In consideration of the license(s) granted hereunder, Formulatrix
            may identify Customer in published lists of licensees of Formulatrix
            software. Customer may, upon request, review such materials in
            advance of publication. Except as required by law, neither party may
            disclose the terms of this Agreement or the work described in it
            without the written consent of the other party.
          </p>
        </div>
      </Modal.Body>
      <Modal.Footer className="eula-modal-footer">
        <CheckBox
          id="eula-initial-config-agree"
          name="eula-initial-config-agree"
          label="I have read and I agree with the terms and conditions."
          checked={agree}
          onChange={() => {
            setAgree(!agree);
          }}
          colorVariant="alternate"
        />
        <MtiButton onClick={onClickOk} disabled={!agree}>OK</MtiButton>
      </Modal.Footer>
    </Modal>
  );
}
